import React, {useEffect, useState} from 'react';
import {Button, ButtonGroup, useToast} from '@chakra-ui/react';
import {ObjectId} from 'bson';
import {
  useIsEnrichingAgent,
  useNumAccountsToEnrich,
  useRunEnrichment,
} from '../../../hooks/api/enrichment';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {getRecursiveFieldCreditCost} from '../../../shared/credits';
import {pluralize} from '../../../lib/helpers';
import {useCustomer} from '../../../hooks/api/metadata';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {useAgentBuilderServerState} from './AgentBuilder.state';
import {FieldDefinition, getFieldLabel} from '../../../shared/enrichment';
import {Download} from '@carbon/icons-react';
import {useDownloadPrompts} from '../../../hooks/api/agents';
import {Parser} from '@json2csv/plainjs';
import FileSaver from 'file-saver';

function usePreviewCreditsUsage({
  numAccounts,
  unenrichedAccounts,
}: {
  numAccounts: number;
  unenrichedAccounts: number;
}) {
  const fieldDefinitions = useCustomer().fieldDefinitions;
  const {
    hasChanges,
    serverState: fieldServerState,
    config,
  } = useFieldDefinitionEditorStore();

  if (!config) {
    return 0;
  }

  if (!fieldServerState) {
    const creditsPerAccount = getRecursiveFieldCreditCost({
      field: {
        config,
        type: 'ai',
        // Hack to use this function with a partial field definition
      } as FieldDefinition,
      fields: fieldDefinitions,
    });

    return numAccounts * creditsPerAccount;
  }

  // Otherwise, we do have a valid field definition, so check to see how many
  // accounts need to be enriched
  const accountsMissingData = hasChanges() ? numAccounts : unenrichedAccounts;

  return (
    accountsMissingData *
    getRecursiveFieldCreditCost({
      field: fieldServerState,
      fields: fieldDefinitions,
    })
  );
}

export const AgentBuilderPreviewButton = ({
  accountIds,
}: {
  accountIds: ObjectId[];
}) => {
  const {
    isSaveable,
    publishChanges,
    serverState: fieldServerState,
  } = useFieldDefinitionEditorStore();
  const {id: agentId} = useAgentBuilderServerState();
  const {id: fieldId} = fieldServerState ?? {};
  const numAccountsToEnrich = useNumAccountsToEnrich();
  const runEnrichment = useRunEnrichment();
  const {data: isEnrichingAgent, isPending: isEnrichingAgentPending} =
    useIsEnrichingAgent({agentId});
  const isKeyplayAdmin = useIsKeyplayAdmin();

  const {
    mutate: numAccountsToEnrichMutation,
    data: {numAccounts: unenrichedAccounts} = {numAccounts: 0},
  } = numAccountsToEnrich;

  useEffect(() => {
    if (fieldId) {
      numAccountsToEnrichMutation({
        accountSelection: {
          name: 'id',
          ids: accountIds,
        },
        fieldDefinitionId: fieldId,
        overrideFieldValues: false,
        preview: true,
      });
    }
    // dependency on isEnrichingAgent because we need to refetch when an enrichment finishes
  }, [fieldId, accountIds, numAccountsToEnrichMutation, isEnrichingAgent]);

  const creditsNeeded = usePreviewCreditsUsage({
    numAccounts: accountIds.length,
    unenrichedAccounts,
  });

  const runPreview = () => {
    publishChanges.mutate(undefined, {
      onSuccess: (updatedFieldDefinition) => {
        if (!updatedFieldDefinition) {
          return;
        }

        runEnrichment.mutate({
          accountSelection: {
            name: 'id',
            ids: accountIds,
          },
          fieldDefinitionId: updatedFieldDefinition.id,
          overrideFieldValues: false,
          bypassCreditCheck: isKeyplayAdmin,
          preview: true,
        });
      },
    });
  };

  const isRunPreviewButtonLoading =
    isEnrichingAgent ||
    isEnrichingAgentPending ||
    numAccountsToEnrich.isPending ||
    publishChanges.isPending ||
    runEnrichment.isPending;

  return (
    <ButtonGroup>
      {isKeyplayAdmin && (
        <DownloadPromptsButton isDisabled={isRunPreviewButtonLoading} />
      )}

      <Button
        width="200px"
        colorScheme="kbuttonblue"
        isDisabled={!creditsNeeded || !isSaveable()}
        isLoading={isRunPreviewButtonLoading}
        onClick={runPreview}
      >
        {creditsNeeded && isSaveable()
          ? `Run Preview (${pluralize(creditsNeeded, 'credit', 'credits')})`
          : 'Run Preview'}
      </Button>
    </ButtonGroup>
  );
};

const DownloadPromptsButton = ({isDisabled}: {isDisabled: boolean}) => {
  const customer = useCustomer();
  const {id: agentId} = useAgentBuilderServerState();
  const {serverState: fieldServerState} = useFieldDefinitionEditorStore();

  const [isDownloadingPrompts, setIsDownloadingPrompts] = useState(false);
  const {
    data: accountPrompts,
    isError: isDownloadingPromptsError,
    isFetching: isDownloadingPromptsFetching,
    isSuccess: isDownloadingPromptsSuccess,
  } = useDownloadPrompts(
    fieldServerState
      ? {agentId, fieldDefinition: fieldServerState}
      : 'skipToken',
    {
      enabled: fieldServerState && isDownloadingPrompts,
    }
  );

  const toast = useToast();
  useEffect(() => {
    if (isDownloadingPromptsError) {
      toast({
        title: 'Error downloading prompts.',
        description: 'Please try again or contact support.',
        status: 'error',
      });
      setIsDownloadingPrompts(false);
    }
  }, [isDownloadingPromptsError, isDownloadingPrompts, toast]);

  useEffect(() => {
    if (
      !isDownloadingPrompts ||
      isDownloadingPromptsFetching ||
      !isDownloadingPromptsSuccess ||
      !fieldServerState
    ) {
      return;
    }

    const csv = new Parser().parse(accountPrompts);
    const blob = new Blob([csv], {type: 'text/csv'});
    FileSaver.saveAs(
      blob,
      `Account Prompts-${customer.name}-${getFieldLabel(fieldServerState)}`
    );

    setIsDownloadingPrompts(false);
  }, [
    accountPrompts,
    customer.name,
    fieldServerState,
    isDownloadingPrompts,
    isDownloadingPromptsFetching,
    isDownloadingPromptsSuccess,
  ]);

  return (
    <Button
      colorScheme="kbuttonblue"
      variant="ghost"
      opacity={0.5}
      onClick={() => setIsDownloadingPrompts(true)}
      isDisabled={!fieldServerState || isDisabled}
      isLoading={isDownloadingPrompts}
    >
      <Download />
    </Button>
  );
};
